import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, SquarePen, Send, ClipboardList, XCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { issueNoteService } from '../../services/issueNoteService';
import {
  IssueNoteDTO,
  IssueNoteApprovalStatusDTO,
  IssueNoteApprovalDTO,
  IssueNoteStatus,
} from '../../types';
import './IssueNotePage.css';

import { getServerErrorMessage } from '../../utils/errorUtils';
interface ApprovalLevelRowProps {
  approval:      IssueNoteApprovalDTO;
  currentUserId: number;
  onApprove:     (id: number) => void;
  onReject:      (id: number) => void;
}

const ApprovalLevelRow: React.FC<ApprovalLevelRowProps> = ({
  approval, currentUserId, onApprove, onReject,
}) => {
  const { t } = useTranslation();
  const isMine = approval.assignedToUserId === currentUserId && approval.status === 'Pending';

  const iconClass =
    approval.status === 'Approved' ? 'isn-level-icon--approved' :
    approval.status === 'Rejected' ? 'isn-level-icon--rejected' : 'isn-level-icon--pending';

  const iconContent =
    approval.status === 'Approved' ? '✓' :
    approval.status === 'Rejected' ? '✗' : approval.approvalLevel.toString();

  return (
    <div className={`isn-level-row ${isMine ? 'isn-level-row--mine' : ''}`}>
      <div className={`isn-level-icon ${iconClass}`}>{iconContent}</div>
      <div className="isn-level-info">
        <div className="isn-level-title">
          {t('issueNote.approvalLevel', 'Level')} {approval.approvalLevel}
          {isMine && (
            <span className="isn-level-mine-badge" style={{ marginInlineStart: 8 }}>
              {t('issueNote.yourApproval', 'Your Approval')}
            </span>
          )}
        </div>
        <div className="isn-level-sub">
          {approval.status === 'Approved' && (
            <span>✓ {t('issueNote.approvedBy', 'Approved by')}: {approval.approvingUserName ?? approval.approvingUserId}
              {approval.approvedAt && ` · ${new Date(approval.approvedAt).toLocaleDateString()}`}
              {approval.approvalComments && ` · "${approval.approvalComments}"`}
            </span>
          )}
          {approval.status === 'Rejected' && (
            <span style={{ color: '#991b1b' }}>
              ✗ {t('issueNote.rejectedBy', 'Rejected by')}: {approval.approvingUserName}
              {approval.rejectionReason && ` · ${approval.rejectionReason}`}
            </span>
          )}
          {approval.status === 'Pending' && (
            <span>{t('issueNote.assignedTo', 'Assigned to')}: {approval.assignedToUserName ?? approval.assignedToUserId}</span>
          )}
        </div>
      </div>
      {isMine && (
        <div className="isn-level-actions">
          <button className="ims-action-btn ims-action-btn--approve" onClick={() => onApprove(approval.id)}>
            {t('common.approve')}
          </button>
          <button className="ims-action-btn ims-action-btn--reject" onClick={() => onReject(approval.id)}>
            {t('common.reject')}
          </button>
        </div>
      )}
    </div>
  );
};

const IssueNoteDetailPage: React.FC = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t }    = useTranslation();
  const { user } = useAuth();

  const STATUS_LABEL: Record<IssueNoteStatus, string> = {
    Draft:           t('statuses.draft'),
    PendingApproval: t('statuses.pendingApproval'),
    Approved:        t('statuses.approved'),
    Rejected:        t('statuses.rejected'),
    Posted:          t('statuses.posted'),
    Cancelled:       t('statuses.cancelled'),
  };

  const STATUS_CLASS: Record<IssueNoteStatus, string> = {
    Draft:           'ims-badge isn-badge-draft',
    PendingApproval: 'ims-badge isn-badge-pending',
    Approved:        'ims-badge isn-badge-approved',
    Rejected:        'ims-badge isn-badge-rejected',
    Posted:          'ims-badge isn-badge-posted',
    Cancelled:       'ims-badge isn-badge-cancelled',
  };

  const [issueNote,      setIssueNote]      = useState<IssueNoteDTO | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<IssueNoteApprovalStatusDTO | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [actionLoading,  setActionLoading]  = useState(false);
  const [error,          setError]          = useState<string | null>(null);

  const [showApproveModal,   setShowApproveModal]   = useState(false);
  const [showRejectModal,    setShowRejectModal]     = useState(false);
  const [pendingApprovalId,  setPendingApprovalId]  = useState<number | null>(null);
  const [approveComments,    setApproveComments]    = useState('');
  const [rejectReason,       setRejectReason]       = useState('');

  const userId = user?.id ?? 0;

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [isnRes, aRes] = await Promise.all([
        issueNoteService.getById(id),
        issueNoteService.getApprovalStatus(id),
      ]);
      const isn = isnRes.data?.data;
      if (isn) setIssueNote(isn);
      const a = aRes.data?.data;
      if (a) setApprovalStatus(a);
    } catch {
      setError(t('issueNote.failedToLoad', 'Failed to load issue note.'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const myPendingApproval = approvalStatus?.approvals.find(
    (a) => a.assignedToUserId === userId && a.status === 'Pending'
  ) ?? null;

  const handleSubmit = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await issueNoteService.submit(id, userId);
      await fetchAll();
    } catch (e: unknown) {
      const err = e as any;
      setError(getServerErrorMessage(err) ?? t('issueNote.failedToSubmit', 'Failed to submit.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveConfirm = async () => {
    if (!pendingApprovalId) return;
    setActionLoading(true);
    try {
      await issueNoteService.approve(pendingApprovalId, userId, approveComments || undefined);
      setShowApproveModal(false);
      await fetchAll();
    } catch (e: unknown) {
      setError(getServerErrorMessage(e) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!pendingApprovalId || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await issueNoteService.reject(pendingApprovalId, userId, rejectReason);
      setShowRejectModal(false);
      await fetchAll();
    } catch (e: unknown) {
      setError(getServerErrorMessage(e) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePost = async () => {
    if (!id || !window.confirm(t('issueNote.confirmPost', 'Post this issue note to inventory?'))) return;
    setActionLoading(true);
    try {
      await issueNoteService.post(id, userId);
      await fetchAll();
    } catch (e: unknown) {
      setError(getServerErrorMessage(e) ?? t('issueNote.failedToPost', 'Failed to post.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!id || !window.confirm(t('issueNote.confirmCancel', 'Cancel this issue note?'))) return;
    setActionLoading(true);
    try {
      await issueNoteService.cancel(id);
      await fetchAll();
    } catch (e: unknown) {
      setError(getServerErrorMessage(e) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return (
    <Layout><div className="isn-page"><div className="ims-loading">{t('common.loading')}</div></div></Layout>
  );

  if (!issueNote) return (
    <Layout><div className="isn-page"><div className="ims-alert ims-alert-error">{error ?? t('issueNote.notFound', 'Issue note not found.')}</div></div></Layout>
  );

  const status    = issueNote.status as IssueNoteStatus;
  const canEdit   = status === 'Draft';
  const canSubmit = status === 'Draft' || status === 'Rejected';
  const canPost   = status === 'Approved';
  const canCancel = ['Draft', 'PendingApproval', 'Approved', 'Rejected'].includes(status);

  const steps = [
    { label: t('issueNote.stepDraft',    'Draft'),    done: true },
    { label: t('issueNote.stepApproval', 'Approval'), done: ['Approved', 'Posted'].includes(status) },
    { label: t('issueNote.stepPosted',   'Posted'),   done: status === 'Posted' },
  ];

  return (
    <Layout>
      <div className="ims-detail-page isn-page">

        {/* Hero */}
        <div className="ims-hero isn-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/issue-notes')}>
              <ArrowLeft size={15} strokeWidth={2} /> {t('common.back')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{issueNote.issueNoteNumber}</h1>
              <p className="ims-hero-supplier">{issueNote.warehouseName}</p>
              <div className="ims-hero-badges">
                <span className={STATUS_CLASS[status] || 'ims-badge'}>{STATUS_LABEL[status] ?? status}</span>
              </div>
            </div>
            <div className="ims-hero-meta">
              <span className="ims-hero-date">{new Date(issueNote.issueDate).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="ims-steps">
          {steps.map((step, i) => (
            <React.Fragment key={step.label}>
              <div className={`ims-step ${step.done ? 'ims-step--done' : ''}`}>
                <div className="ims-step-dot">{step.done ? '✓' : i + 1}</div>
                {step.label}
              </div>
              {i < steps.length - 1 && <span className="ims-step-arrow">›</span>}
            </React.Fragment>
          ))}
        </div>

        {error && <div className="ims-alert ims-alert-error" style={{ margin: '0 20px 8px' }}>{error}</div>}

        <div className="ims-detail-body">

          {/* Main */}
          <div className="ims-detail-main">

            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('issueNote.issueNoteDetails', 'Issue Note Details')}</span>
              </div>
              <div className="ims-info-grid">
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('issueNote.warehouse', 'Warehouse')}</span>
                  <span className="ims-info-value">{issueNote.warehouseName}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('issueNote.issueDate', 'Issue Date')}</span>
                  <span className="ims-info-value">{new Date(issueNote.issueDate).toLocaleDateString()}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('issueNote.issuedBy', 'Issued By')}</span>
                  <span className="ims-info-value">{issueNote.issuedByName}</span>
                </div>
                {issueNote.postedByName && (
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('issueNote.postedBy', 'Posted By')}</span>
                    <span className="ims-info-value">
                      {issueNote.postedByName}
                      {issueNote.postedAt && ` · ${new Date(issueNote.postedAt).toLocaleDateString()}`}
                    </span>
                  </div>
                )}
                {issueNote.notes && (
                  <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="ims-info-label">{t('common.notes')}</span>
                    <span className="ims-info-value">{issueNote.notes}</span>
                  </div>
                )}
                {issueNote.rejectionNotes && (
                  <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="ims-info-label">{t('issueNote.rejectionNotes', 'Rejection Notes')}</span>
                    <span className="ims-info-value" style={{ color: '#991b1b' }}>{issueNote.rejectionNotes}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Items */}
            <div className="ims-card" style={{ marginTop: 16 }}>
              <div className="ims-card-header">
                <span className="ims-card-title">{t('issueNote.issuedItems', 'Issued Items')}</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="ims-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('issueNote.product', 'Product')}</th>
                      <th>{t('issueNote.unit', 'Unit')}</th>
                      <th style={{ textAlign: 'right' }}>{t('issueNote.quantity', 'Quantity')}</th>
                      <th>{t('issueNote.purpose', 'Purpose')}</th>
                      <th>{t('issueNote.itemNotes', 'Notes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issueNote.items.map((item, idx) => (
                      <tr key={item.id}>
                        <td>{idx + 1}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{item.productName}</div>
                          {item.productCode && <div style={{ fontSize: '0.75rem', color: 'var(--ims-text-muted)' }}>{item.productCode}</div>}
                        </td>
                        <td>{item.unitName}</td>
                        <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                        <td style={{ fontSize: '0.85rem' }}>{item.purpose ?? '—'}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--ims-text-sub)' }}>{item.notes ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid var(--ims-border)', gap: 16 }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--ims-text-sub)' }}>{t('issueNote.totalItems', 'Total items')}</span>
                <span style={{ fontWeight: 700, color: 'var(--ims-raw-purple-700)' }}>{issueNote.items.length}</span>
              </div>
            </div>

            {/* Approval levels */}
            {approvalStatus && approvalStatus.approvals.length > 0 && (
              <div className="ims-card" style={{ marginTop: 16 }}>
                <div className="ims-card-header">
                  <span className="ims-card-title">{t('issueNote.approvalLevels', 'Approval Levels')}</span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--ims-text-muted)' }}>
                    {approvalStatus.currentApprovalLevel}/{approvalStatus.totalApprovalLevels} {t('issueNote.levelsCompleted', 'completed')}
                  </span>
                </div>
                <div className="ims-card-body">
                  {myPendingApproval && (
                    <div className="isn-pending-banner">⏳ {t('issueNote.pendingYourApproval', 'Pending your approval')}</div>
                  )}
                  <div className="isn-levels-list">
                    {approvalStatus.approvals.map((a) => (
                      <ApprovalLevelRow
                        key={a.id}
                        approval={a}
                        currentUserId={userId}
                        onApprove={(aid) => { setPendingApprovalId(aid); setApproveComments(''); setShowApproveModal(true); }}
                        onReject={(aid)  => { setPendingApprovalId(aid); setRejectReason('');   setShowRejectModal(true); }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="ims-detail-sidebar">

            <div className="ims-card">
              <div className="ims-card-header"><span className="ims-card-title">{t('common.actions')}</span></div>
              <div className="ims-card-body">
                <div className="ims-actions-list">
                  {canEdit && (
                    <button className="ims-action-btn ims-action-btn--edit" onClick={() => navigate(`/issue-notes/${id}/edit`)}>
                      <SquarePen size={15} strokeWidth={2} /> {t('common.edit')}
                    </button>
                  )}
                  {canSubmit && (
                    <button className="ims-action-btn ims-action-btn--approve" onClick={handleSubmit} disabled={actionLoading}>
                      <Send size={15} strokeWidth={2} /> {t('issueNote.submitForApproval', 'Submit for Approval')}
                    </button>
                  )}
                  {status === 'PendingApproval' && !myPendingApproval && (
                    <div className="ims-action-btn" style={{ background: '#f3f4f6', color: '#4b5563', cursor: 'default' }}>
                      ⏳ {t('issueNote.awaitingApproval', 'Awaiting approval')}
                    </div>
                  )}
                  {canPost && (
                    <button className="ims-action-btn ims-action-btn--issue-post" onClick={handlePost} disabled={actionLoading}>
                      <ClipboardList size={15} strokeWidth={2} /> {t('issueNote.postIssueNote', 'Post Issue Note')}
                    </button>
                  )}
                  {canCancel && (
                    <button className="ims-action-btn ims-action-btn--reject" onClick={handleCancel} disabled={actionLoading}>
                      <XCircle size={15} strokeWidth={2} /> {t('issueNote.cancel', 'Cancel')}
                    </button>
                  )}
                  <button className="ims-action-btn ims-action-btn--edit" onClick={() => navigate('/issue-notes')}>
                    <ArrowLeft size={15} strokeWidth={2} /> {t('common.back')}
                  </button>
                </div>
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header"><span className="ims-card-title">{t('issueNote.summary', 'Summary')}</span></div>
              <div className="ims-card-body">
                <div className="ims-info-grid" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('issueNote.totalItems', 'Total Items')}</span>
                    <span className="ims-info-value" style={{ fontWeight: 700 }}>{issueNote.items.length}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="ims-modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="ims-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ims-modal-header">
              <h3>{t('issueNote.approveTitle', 'Approve Issue Note')}</h3>
              <button className="ims-modal-close" onClick={() => setShowApproveModal(false)}>×</button>
            </div>
            <div className="ims-modal-body">
              <div className="ims-form-group">
                <label className="ims-form-label">{t('issueNote.approvalComments', 'Comments')} ({t('common.optional')})</label>
                <textarea className="ims-form-control" rows={3} value={approveComments} onChange={(e) => setApproveComments(e.target.value)} placeholder={t('issueNote.approvalCommentsPlaceholder', 'Optional approval comments…')} />
              </div>
            </div>
            <div className="ims-modal-footer">
              <button className="btn-cancel" onClick={() => setShowApproveModal(false)}>{t('common.cancel')}</button>
              <button className="ims-btn ims-btn-success" onClick={handleApproveConfirm} disabled={actionLoading}>
                {actionLoading ? '…' : t('common.approve')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="ims-modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="ims-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ims-modal-header">
              <h3>{t('issueNote.rejectTitle', 'Reject Issue Note')}</h3>
              <button className="ims-modal-close" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            <div className="ims-modal-body">
              <div className="ims-form-group">
                <label className="ims-form-label">{t('issueNote.rejectionReason', 'Rejection Reason')} <span className="required">*</span></label>
                <textarea className="ims-form-control" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t('issueNote.rejectionReasonPlaceholder', 'State the reason for rejection…')} />
              </div>
            </div>
            <div className="ims-modal-footer">
              <button className="btn-cancel" onClick={() => setShowRejectModal(false)}>{t('common.cancel')}</button>
              <button className="btn-delete" onClick={handleRejectConfirm} disabled={!rejectReason.trim() || actionLoading}>
                {actionLoading ? '…' : t('common.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default IssueNoteDetailPage;
